import React, { useEffect, useRef, useState } from 'react';
import {
  Image,
  Platform,
  StyleSheet,
  View,
  type ImageResizeMode,
  type StyleProp,
  type ImageStyle,
} from 'react-native';

type LazyStorageImageProps = {
  uri: string | null | undefined;
  style?: StyleProp<ImageStyle>;
  resizeMode?: ImageResizeMode;
  placeholderColor?: string;
  /** Solo web: precarga antes de entrar al viewport (p. ej. "200px"). */
  rootMargin?: string;
};

/**
 * Imagen que en web solo descarga cuando entra (casi) en pantalla.
 * En iOS/Android carga de inmediato.
 */
export function LazyStorageImage({
  uri,
  style,
  resizeMode = 'cover',
  placeholderColor = '#e5e7eb',
  rootMargin = '200px',
}: LazyStorageImageProps) {
  const [shouldLoad, setShouldLoad] = useState(Platform.OS !== 'web');
  const hostRef = useRef<View>(null);

  useEffect(() => {
    if (Platform.OS !== 'web' || shouldLoad || !uri) return;

    if (typeof IntersectionObserver === 'undefined') {
      setShouldLoad(true);
      return;
    }

    const target = hostRef.current as unknown as Element | null;
    if (!target) {
      setShouldLoad(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setShouldLoad(true);
          observer.disconnect();
        }
      },
      { rootMargin, threshold: 0.01 }
    );

    observer.observe(target);
    return () => observer.disconnect();
  }, [uri, rootMargin, shouldLoad]);

  if (!uri) return null;

  return (
    <View ref={hostRef} style={style} collapsable={false}>
      {shouldLoad ? (
        <Image source={{ uri }} style={StyleSheet.absoluteFillObject} resizeMode={resizeMode} />
      ) : (
        <View style={[StyleSheet.absoluteFillObject, { backgroundColor: placeholderColor }]} />
      )}
    </View>
  );
}
